/**
 * Pool Reports Service
 *
 * Handles API communication for monthly and annual pool reports
 */

import { ApiClient } from '@/config/axios.config';
import type {
  Pool,
  ReportList,
  ReportType,
  Downloadable,
  ReportsHeading,
  Report,
  Contribution,
  Vessel,
  VesselDetails,
  VesselPoolPoints,
  VesselMonthlyInfo,
  VesselVoyageInfo,
  VesselVoyageNote,
  VesselBunkerInfo,
  NetAllocation,
  Offhire,
  DistributionDate,
  Distribution,
} from '@/types/poolReports.types';

export class PoolReportsService {
  private static readonly BASE_PATH = '/reports';

  /**
   * Get list of available pools
   */
  static async getPools(): Promise<Pool[]> {
    try {
      const url = `${this.BASE_PATH}/pools`;
      const data = await ApiClient.get<Pool[]>(url);
      return data;
    } catch (error) {
      console.error('Failed to fetch pools:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch pools');
    }
  }

  /**
   * Get list of reports for a specific pool and report type
   */
  static async getReportList(reportType: ReportType, poolId: number): Promise<ReportList[]> {
    try {
      const url = `${this.BASE_PATH}/${reportType}?poolId=${poolId}`;
      const data = await ApiClient.get<ReportList[]>(url);
      return data;
    } catch (error) {
      console.error('Failed to fetch report list:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch report list');
    }
  }

  /**
   * Get downloadable files for a specific report
   */
  static async getDownloadables(fileId: number): Promise<Downloadable> {
    try {
      const url = `${this.BASE_PATH}/downloads/${fileId}`;
      const data = await ApiClient.get<Downloadable>(url);
      return data;
    } catch (error) {
      console.error('Failed to fetch downloadables:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch downloadables');
    }
  }

  /**
   * Download a report file (Excel or XML)
   */
  static async downloadFile(
    fileId: number,
    type: 'xml' | 'excel'
  ): Promise<{ blob: Blob; fileName: string }> {
    try {
      const url = `${this.BASE_PATH}/downloads/${fileId}/${type}`;
      const response = await ApiClient.downloadWithHeaders(url);

      // Extract filename from content-disposition header
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `report.${type === 'xml' ? 'xml' : 'xlsx'}`;

      if (contentDisposition) {
        // Angular extracts: .split('filename="')[1].slice(0, -1)
        const parts = contentDisposition.split('filename="');
        if (parts.length > 1) {
          fileName = parts[1].slice(0, -1);
        }
      }

      return {
        blob: response.data,
        fileName,
      };
    } catch (error) {
      console.error('Failed to download file:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to download file');
    }
  }

  /**
   * Get report headings (summary metrics) for a specific report
   */
  static async getHeadings(fileId: number, reportType: ReportType): Promise<ReportsHeading[]> {
    try {
      const url = `${this.BASE_PATH}/${reportType}/headers/${fileId}`;
      const data = await ApiClient.get<ReportsHeading[]>(url);
      return data;
    } catch (error) {
      console.error('Failed to fetch report headings:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch report headings');
    }
  }

  /**
   * Get report details (quarterly/monthly breakdown) for a specific report
   */
  static async getDetails(fileId: number, reportType: ReportType): Promise<Report[]> {
    try {
      const url = `${this.BASE_PATH}/${reportType}/details/${fileId}`;
      const data = await ApiClient.get<Report[]>(url);
      return data;
    } catch (error) {
      console.error('Failed to fetch report details:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch report details');
    }
  }

  /**
   * Get contribution reports for a specific file
   */
  static async getContributions(fileId: number): Promise<Contribution[]> {
    try {
      const url = `/reports/contributions/${fileId}`;
      const data = await ApiClient.get<Contribution[]>(url);
      return data;
    } catch (error) {
      console.error('Failed to fetch contributions:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch contributions');
    }
  }

  /**
   * Get vessel contributions for a specific owner
   */
  static async getOwnerVessels(fileId: number, ownerId: number): Promise<Vessel[]> {
    try {
      const url = `/reports/contributions/${fileId}/owner/${ownerId}`;
      const data = await ApiClient.get<Vessel[]>(url);
      // Mark vessels as clickable if not Total row
      return data.map((item) => ({
        ...item,
        withLink: item.name !== 'Total Contribution',
      }));
    } catch (error) {
      console.error('Failed to fetch owner vessels:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch owner vessels');
    }
  }

  /**
   * Get vessel details
   */
  static async getVesselDetails(fileId: number, vesselId: number): Promise<VesselDetails> {
    const url = `/reports/contributions/${fileId}/vessel/${vesselId}`;
    return ApiClient.get<VesselDetails>(url);
  }

  /**
   * Get vessel pool points
   */
  static async getVesselPoolPoints(fileId: number, vesselId: number): Promise<VesselPoolPoints[]> {
    const url = `/reports/contributions/${fileId}/vessel/${vesselId}/poolpoints`;
    return ApiClient.get<VesselPoolPoints[]>(url);
  }

  /**
   * Get vessel monthly info
   */
  static async getVesselMonthlyInfo(
    fileId: number,
    vesselId: number
  ): Promise<VesselMonthlyInfo[]> {
    const url = `/reports/contributions/${fileId}/vessel/${vesselId}/monthlyinfo`;
    return ApiClient.get<VesselMonthlyInfo[]>(url);
  }

  /**
   * Get vessel voyage info
   */
  static async getVesselVoyageInfo(fileId: number, vesselId: number): Promise<VesselVoyageInfo[]> {
    const url = `/reports/contributions/${fileId}/vessel/${vesselId}/voyageinfo`;
    return ApiClient.get<VesselVoyageInfo[]>(url);
  }

  /**
   * Get vessel voyage notes
   */
  static async getVesselVoyageNotes(fileId: number, vesselId: number): Promise<VesselVoyageNote[]> {
    const url = `/reports/contributions/${fileId}/vessel/${vesselId}/voyagenotes`;
    return ApiClient.get<VesselVoyageNote[]>(url);
  }

  /**
   * Get vessel bunker info
   */
  static async getVesselBunkerInfo(fileId: number, vesselId: number): Promise<VesselBunkerInfo[]> {
    const url = `/reports/contributions/${fileId}/vessel/${vesselId}/bunkerinfo`;
    return ApiClient.get<VesselBunkerInfo[]>(url);
  }

  /**
   * Get net allocations
   */
  static async getNetAllocations(fileId: number): Promise<NetAllocation[]> {
    const url = `/reports/netallocations/${fileId}`;
    return ApiClient.get<NetAllocation[]>(url);
  }

  /**
   * Get offhires
   */
  static async getOffhires(fileId: number): Promise<Offhire[]> {
    const url = `/reports/offhires/${fileId}`;
    return ApiClient.get<Offhire[]>(url);
  }

  /**
   * Get net allocation owner details
   */
  static async getNetAllocationOwnerDetails(
    fileId: number,
    ownerId: number
  ): Promise<NetAllocation> {
    const url = `/reports/netallocations/${fileId}/owner/${ownerId}/details`;
    return ApiClient.get<NetAllocation>(url);
  }

  /**
   * Get net allocation owner vessels
   */
  static async getNetAllocationOwnerVessels(
    fileId: number,
    ownerId: number
  ): Promise<NetAllocation[]> {
    const url = `/reports/netallocations/${fileId}/owner/${ownerId}`;
    return ApiClient.get<NetAllocation[]>(url);
  }

  /**
   * Get offhire owner details
   */
  static async getOffhireOwnerDetails(fileId: number, ownerId: number): Promise<Offhire> {
    const url = `/reports/offhires/${fileId}/owner/${ownerId}/details`;
    return ApiClient.get<Offhire>(url);
  }

  /**
   * Get offhire owner vessels
   */
  static async getOffhireOwnerVessels(fileId: number, ownerId: number): Promise<Offhire[]> {
    const url = `/reports/offhires/${fileId}/owner/${ownerId}`;

    return ApiClient.get<Offhire[]>(url);
  }

  /**
   * Get offhire vessel breakdown
   */
  static async getOffhireVesselBreakdown(fileId: number, vesselId: number): Promise<Offhire[]> {
    const url = `/reports/offhires/${fileId}/vessel/${vesselId}`;
    return ApiClient.get<Offhire[]>(url);
  }

  /**
   * Get distribution dates for a specific file
   */
  static async getDistributionDates(fileId: number): Promise<DistributionDate[]> {
    try {
      const url = `/reports/distributions/${fileId}/datelist`;
      const data = await ApiClient.get<DistributionDate[]>(url);

      return data;
    } catch (error) {
      console.error('Failed to fetch distribution dates:', error);
      throw new Error(
        error instanceof Error ? error.message : 'Failed to fetch distribution dates'
      );
    }
  }

  /**
   * Get distributions for a specific file and date
   */
  static async getDistributions(fileId: number, date: string): Promise<Distribution[]> {
    try {
      const url = `/reports/distributions/${fileId}/date/${date}`;
      const data = await ApiClient.get<Distribution[]>(url);

      return data;
    } catch (error) {
      console.error('Failed to fetch distributions:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch distributions');
    }
  }

  /**
   * Get distributions for a specific owner
   */
  static async getDistributionsByOwnerId(
    fileId: number,
    ownerId: number,
    date: string
  ): Promise<Distribution[]> {
    try {
      const url = `/reports/distributions/${fileId}/owner/${ownerId}/date/${date}`;
      const data = await ApiClient.get<Distribution[]>(url);

      return data;
    } catch (error) {
      console.error('Failed to fetch distributions by owner:', error);
      throw new Error(
        error instanceof Error ? error.message : 'Failed to fetch distributions by owner'
      );
    }
  }

  /**
   * Trigger file download in browser
   */
  static triggerDownload(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    document.body.appendChild(link);
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    link.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
    link.remove();
  }
}
